import React from "react";
import { AlertTriangle, Home, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";

type ErrorBoundaryProps = {
  children: React.ReactNode;
  fallbackTitle?: string;
};

type ErrorBoundaryState = {
  hasError: boolean;
  error?: Error;
};

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = {
    hasError: false,
  };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Application render error:", error, errorInfo);
  }

  private reloadPage = () => {
    window.location.reload();
  };

  private goHome = () => {
    window.location.href = "/";
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="w-full max-w-lg rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-red-50 p-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-slate-900">
                {this.props.fallbackTitle || "This page could not load"}
              </h1>
              <p className="mt-2 text-sm text-slate-600">
                Refresh the page or return home. The error was also logged in the browser console.
              </p>
            </div>
          </div>

          {this.state.error?.message && (
            <pre className="mt-4 max-h-32 overflow-auto rounded-md bg-slate-100 p-3 text-xs text-slate-600">
              {this.state.error.message}
            </pre>
          )}

          <div className="mt-5 flex flex-wrap gap-2">
            <Button onClick={this.reloadPage}>
              <RotateCcw className="mr-2 h-4 w-4" />
              Reload
            </Button>
            <Button variant="outline" onClick={this.goHome}>
              <Home className="mr-2 h-4 w-4" />
              Home
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
